import { z } from 'zod';

/** Supported HealthPathways country codes */
export const HEALTHPATHWAYS_COUNTRIES = ['au', 'nz', 'gb', 'ca'] as const;

// First-pass shape check so obvious typos (e.g. an AU postcode entered into a
// GB form, or a 7-digit number) fail fast in the dialog. The upstream MCP
// silently falls back to a major-city zone for anything containing ≥1 digit,
// so without this the user wouldn't know they'd been routed to Sydney/Auckland.
// True existence is verified BE-side via pgeocode before calling the MCP.
const POSTCODE_PATTERNS: Record<string, RegExp> = {
  au: /^\d{4}$/,
  nz: /^\d{4}$/,
  gb: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,
  ca: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
};

/** Create a localized Zod schema for the setup form */
export function createHealthPathwaysSetupSchema(messages: {
  countryRequired: string;
  postcodeRequired: string;
  postcodeInvalidFormat: string;
}) {
  return z
    .object({
      country_code: z.string().min(1, messages.countryRequired),
      postcode: z.string().min(1, messages.postcodeRequired),
      zone_type: z.enum(['hospital', 'community']),
    })
    .superRefine((data, ctx) => {
      const trimmed = data.postcode.trim();
      if (!trimmed) return; // `min(1)` already surfaced; don't double-report
      const pattern = POSTCODE_PATTERNS[data.country_code.toLowerCase()];
      if (pattern && !pattern.test(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['postcode'],
          message: messages.postcodeInvalidFormat,
        });
      }
    });
}

export type HealthPathwaysSetupFormValues = z.infer<
  ReturnType<typeof createHealthPathwaysSetupSchema>
>;
